import { useState } from "react";
import { Outlet } from "react-router";
import { Clock } from "lucide-react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useAuth } from "../../../auth/hooks";
import { AccountMenu } from "../../AccountMenu";
import { Separator } from "../../primitives/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "../../primitives/sidebar";
import { Toaster } from "../../primitives/sonner";
import { FeedbackWidget } from "../feedback/FeedbackWidget";
import { NotificationBell } from "../notifications/NotificationBell";
import { FindBox } from "./FindBox";
import { ObjectSidebar } from "./ObjectSidebar";

/** Per device: whether One's sidebar was left open. */
export const SIDEBAR_OPEN_STORAGE_KEY = "cis.sidebar_open";

/**
 * One's page frame (04_frontend_spec.md §6.1): the object sidebar, and a
 * header strip with Find, notifications and the account menu, plus, on a
 * phone only, the button that opens the sidebar's drawer (on a desktop the
 * collapse control sits in the sidebar's own header). Classic keeps
 * `DashboardLayout`; `AppLayout` picks between them.
 *
 * The page covers the whole screen on a phone (`viewport-fit=cover` in
 * index.html), so the header is padded below the status bar and the page's
 * end above the home indicator and Safari's floating toolbar (the sides are
 * padded on `body`, styles/index.css).
 */
export function OneLayout() {
  const { user } = useAuth();
  const capabilities = usePlatformCapabilities();
  // shadcn writes its state to a cookie it never reads back; the preference
  // lives in per-device storage with the other UI choices, so the provider
  // is controlled.
  const [isSidebarOpen, setIsSidebarOpen] = useState(
    () => capabilities.getStorageItem(SIDEBAR_OPEN_STORAGE_KEY) !== "closed",
  );

  return (
    <SidebarProvider
      open={isSidebarOpen}
      onOpenChange={(open) => {
        setIsSidebarOpen(open);
        capabilities.setStorageItem(
          SIDEBAR_OPEN_STORAGE_KEY,
          open ? null : "closed",
        );
      }}
    >
      <ObjectSidebar />
      <SidebarInset className="min-w-0">
        <header className="flex items-center gap-3 border-b border-border bg-surface px-4 pb-2.5 pt-[calc(0.625rem+env(safe-area-inset-top))] sm:px-6">
          <SidebarTrigger className="size-8 md:hidden" />
          <Separator orientation="vertical" className="h-5 md:hidden" />
          <div className="max-w-lg flex-1">
            <FindBox />
          </div>
          <div className="ml-auto flex items-center gap-1">
            <NotificationBell />
            <AccountMenu />
          </div>
        </header>
        {/* The 6rem of bottom padding clears the fixed Feedback button, as in Classic. */}
        <div className="mx-auto w-full max-w-[1400px] px-4 pb-[calc(6rem+env(safe-area-inset-bottom))] pt-6 sm:px-8 sm:pt-8">
          {user?.role === "pending" ? (
            <div className="flex min-h-[400px] flex-col items-center justify-center text-center">
              <Clock className="mb-4 h-12 w-12 text-amber-500" />
              <h2 className="mb-2 text-xl font-semibold">
                Account awaiting activation
              </h2>
              <p className="max-w-md text-muted-foreground">
                Your account has been created but is not yet active. An
                administrator will assign you a role and portfolio shortly.
              </p>
            </div>
          ) : (
            <Outlet />
          )}
        </div>
      </SidebarInset>
      <Toaster />
      <FeedbackWidget />
    </SidebarProvider>
  );
}
